import { css, cx } from "@emotion/css"

import { AgencyMember } from "models/agency"
import { ReactComponent as CompaniesIcon } from "assets/icon-team.svg"
import { Container } from "components/Page"
import { EmptyState } from "components/EmptyState"
import { Loading } from "components/Loading"
import { fetchAgency } from "api/agencies"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useQuery } from "react-query"

export const Team = () => {
  const { t } = useI18n()
  const { id } = useParams<{ id: string }>()

  // ** fetch teams **00
  const { data: agency, isLoading } = useQuery(
    ["market-agency", id],
    fetchAgency,
  )

  const { agencyAdmins, agencyFinances, managingDirectors } = agency || {}
  const nobody =
    !agencyAdmins?.length &&
    !agencyFinances?.length &&
    !managingDirectors?.length

  return isLoading ? (
    <Loading />
  ) : (
    <Container
      className={cx(styles.container, { [styles.noDataContainer]: nobody })}
    >
      <div>
        {!!agencyAdmins?.length ? (
          <>
            <div className={cx("BodyBody-1BlackBold", styles.title)}>
              {t("role.AGENCY_ADMINISTRATOR")}
            </div>
            <div className={styles.cards}>
              {agencyAdmins.map((agencyAdmin) => (
                <MemberCard
                  key={agencyAdmin.id}
                  contactTitle={t("role.AGENCY_ADMINISTRATOR")}
                  member={agencyAdmin}
                />
              ))}
            </div>
          </>
        ) : null}
        {!!agencyFinances?.length ? (
          <>
            <div className={cx("BodyBody-1BlackBold", styles.title)}>
              {t("role.list.AGENCY_FINANCE")}
            </div>
            <div className={styles.cards}>
              {agencyFinances.map((agencyFinance) => (
                <MemberCard
                  key={agencyFinance.id}
                  contactTitle={t("role.AGENCY_FINANCE")}
                  member={agencyFinance}
                />
              ))}
            </div>
          </>
        ) : null}
        {!!managingDirectors?.length ? (
          <>
            <div className={cx("BodyBody-1BlackBold", styles.title)}>
              {t("role.MANAGING_DIRECTOR")}
            </div>
            <div className={styles.cards}>
              {managingDirectors.map((managingDirector) => (
                <MemberCard
                  key={managingDirector.id}
                  contactTitle={t("role.AGENCY_FINANCE")}
                  member={managingDirector}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
      {nobody ? (
        <EmptyState
          title={t("agencies.team.emptyState.title")}
          icon={<CompaniesIcon />}
        />
      ) : null}
    </Container>
  )
}
interface IMemberCardProps {
  contactTitle: string
  member: AgencyMember
}

const MemberCard = ({ member, contactTitle }: IMemberCardProps) => {
  const { t } = useI18n()
  return (
    <div className={styles.memberCard}>
      <div className={styles.memberCardGroup}>
        <div className="BodyBody-1BlackRegular">
          {[member.firstname, member.lastname].join(" ")}
        </div>
        <div className="umbersNumber-2Primary-mainRegular">{member.mail}</div>
      </div>
      <div className={styles.memberCardGroup}>
        <div className="BodyBody-sBlackRegular">{t("contact.title")}</div>
        <div className="BodyBody-sDark-greyLight">{contactTitle}</div>
      </div>
    </div>
  )
}

const styles = {
  cards: css`
    display: flex;
    gap: var(--spacing-m);
    flex-wrap: wrap;
  `,
  memberCard: css`
    display: grid;
    width: 300px;
    padding: var(--spacing-s);
    border-radius: 3px;
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.16);
    border: solid 1px var(--light-grey);
    background-color: var(--white);
    grid-row-gap: var(--spacing-m);
  `,
  memberCardGroup: css`
    display: grid;
    grid-row-gap: 2px;
  `,
  container: css`
    padding: var(--spacing-xxl-2) 0px var(--spacing-m);
  `,
  noDataContainer: css`
    padding: var(--spacing-xxl-2) 0 0 0;
  `,
  title: css`
    padding-left: var(--spacing-xxs);
    margin-bottom: var(--spacing-m);
    &:not(:first-child) {
      margin-top: var(--spacing-xxl-4);
    }
  `,
}
